import { useId, useRef } from 'react'
import { ShareOptions } from './ShareOptions'
import { useSignatureStatusStore } from '@/features/clinicalDocuments/model/signatureStatusStore'
import { useDocumentShareStore } from '@/shared/lib/documentShareStore'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { buildDocumentShareText, maskBrPhone } from '@/shared/lib/whatsappShare'
import { Button } from '@/shared/ui/Button'
import { CloseIcon } from '@/shared/ui/icons'

/** SHARE-01/02/09 — folha no celular e janela central no computador. */
export function DocumentShareSheet() {
  const pending = useDocumentShareStore((state) => state.pending)
  const dismiss = useDocumentShareStore((state) => state.dismiss)
  const knownSignature = useSignatureStatusStore((state) => pending ? state.byId[pending.documentId] : undefined)
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const { requestClose } = useOverlay(pending !== null, {
    kind: 'sheet',
    label: 'Compartilhar documento',
    onRequestClose: dismiss,
    panelRef,
    initialFocusRef: closeRef,
  })
  const close = () => { requestClose(); dismiss() }
  if (!pending) return null
  const signed = pending.signed || Boolean(knownSignature)
  const phone = maskBrPhone(pending.phoneE164)

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-[130] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onMouseDown={(event) => { if (event.target === event.currentTarget) close() }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="max-h-[90dvh] w-full max-w-[460px] space-y-4 overflow-y-auto rounded-t-2xl bg-[var(--color-bg-surface)] p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-xl sm:rounded-2xl"
      >
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-[var(--color-text-primary)]">Compartilhar</h2>
            <p id={descriptionId} className="break-words text-sm text-[var(--color-text-secondary)]">
              {buildDocumentShareText(pending)}{phone ? ` · ${phone}` : ''}
            </p>
          </div>
          <Button ref={closeRef} variant="ghost" size="icon" aria-label="Fechar" onClick={close}>
            <CloseIcon size={18} aria-hidden="true" />
          </Button>
        </header>
        {!signed ? (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] p-3">
            <p className="text-sm text-[var(--color-text-secondary)]">Este documento vai <strong>sem assinatura digital</strong>.</p>
            {pending.onSign ? <Button variant="secondary" className="shrink-0" onClick={pending.onSign}>Assinar antes</Button> : null}
          </div>
        ) : null}
        <ShareOptions key={pending.documentId} document={{ ...pending, signed }} />
        <p className="text-xs text-[var(--color-text-muted)]">Link para o paciente abrir o PDF. No celular, escolha o WhatsApp no compartilhamento do aparelho. No computador, anexe o PDF baixado à conversa.</p>
      </div>
    </div>
  )
}
